import { shopifyFetch } from '../shopifyConfig';

/**
 * Storefront API service for the "Pumpous Apparel" shop: product browsing
 * and cart management. Checkout itself is NOT handled here -- the cart's
 * `checkoutUrl` is a Shopify-hosted page the UI redirects to for payment
 * (see CLAUDE.md's decision log for why: custom payment-collection UI is
 * locked behind Shopify Plus, so the standard/recommended pattern is cart-
 * in-app, checkout-on-Shopify).
 */

const PRODUCT_FIELDS = `
  id
  title
  handle
  description
  featuredImage {
    url
    altText
  }
  priceRange {
    minVariantPrice {
      amount
      currencyCode
    }
  }
  variants(first: 25) {
    edges {
      node {
        id
        title
        availableForSale
        price {
          amount
          currencyCode
        }
        selectedOptions {
          name
          value
        }
      }
    }
  }
`;

// Flattens the edges/node GraphQL connection shape into a plain array --
// callers shouldn't need to know Storefront API's pagination wrapper.
const normalizeProduct = (node) => ({
  ...node,
  variants: node.variants.edges.map((e) => e.node)
});

// Which collections show in the Shop panel, and in what order, is entirely
// controlled by a hand-curated Shopify navigation menu (Online Store ->
// Navigation -> Menus -> "Pumpous App Shop") -- per direct user decision,
// so reordering/adding/removing collections never needs a code change, just
// dragging links around in that menu.
const SHOP_MENU_HANDLE = "pumpous-app-shop";

export const fetchCollections = async () => {
  try {
    const menuQuery = `
      query FetchShopMenu($handle: String!) {
        menu(handle: $handle) {
          items {
            type
            resourceId
          }
        }
      }
    `;
    const menuData = await shopifyFetch(menuQuery, { handle: SHOP_MENU_HANDLE });
    if (!menuData.menu) return { success: true, collections: [] };

    // Only COLLECTION-type links carry a resolvable collection reference --
    // any other link type in the menu (a raw URL, a product, etc.) is
    // silently skipped rather than erroring, since this menu is meant to be
    // collections-only but shouldn't break if something else sneaks in.
    const collectionIds = menuData.menu.items
      .filter((item) => item.type === "COLLECTION" && item.resourceId)
      .map((item) => item.resourceId);
    if (collectionIds.length === 0) return { success: true, collections: [] };

    // `resourceId`, not the `resource` field itself, is what's actually
    // resolvable via the Storefront API on this store/version -- `resource`
    // returned null for every menu item regardless of API version when
    // tested live, so this batches the ids through `nodes` instead.
    const nodesQuery = `
      query FetchMenuCollections($ids: [ID!]!) {
        nodes(ids: $ids) {
          ... on Collection {
            id
            title
            handle
            image {
              url
              altText
            }
            products(first: 1) {
              edges {
                node { id }
              }
            }
          }
        }
      }
    `;
    const nodesData = await shopifyFetch(nodesQuery, { ids: collectionIds });

    // `nodes` preserves the input id order, but a since-unpublished/deleted
    // collection resolves to `null` in its slot rather than being dropped --
    // filter those out here, same pass that drops empty collections (no
    // `productCount` field exists on Collection, so "at least one product
    // in a first:1 fetch" is the cheapest emptiness check available).
    const collections = nodesData.nodes
      .filter((node) => node && node.products.edges.length > 0)
      .map(({ products, ...rest }) => rest);

    return { success: true, collections };
  } catch (error) {
    console.error("Shop Transmission Error: ", error);
    return { success: false, error };
  }
};

// Fetches one page (24) of products within a single collection, by handle.
export const fetchCollectionProducts = async (handle, cursor = null) => {
  try {
    const query = `
      query FetchCollectionProducts($handle: String!, $cursor: String) {
        collection(handle: $handle) {
          title
          products(first: 24, after: $cursor) {
            edges {
              node {
                ${PRODUCT_FIELDS}
              }
            }
            pageInfo {
              hasNextPage
              endCursor
            }
          }
        }
      }
    `;
    const data = await shopifyFetch(query, { handle, cursor });
    if (!data.collection) return { success: false, error: "Collection not found" };
    const products = data.collection.products.edges.map((e) => normalizeProduct(e.node));
    return {
      success: true,
      title: data.collection.title,
      products,
      hasNextPage: data.collection.products.pageInfo.hasNextPage,
      endCursor: data.collection.products.pageInfo.endCursor
    };
  } catch (error) {
    console.error("Shop Transmission Error: ", error);
    return { success: false, error };
  }
};

const CART_FIELDS = `
  id
  checkoutUrl
  totalQuantity
  cost {
    subtotalAmount { amount currencyCode }
    totalAmount { amount currencyCode }
  }
  lines(first: 50) {
    edges {
      node {
        id
        quantity
        cost {
          totalAmount { amount currencyCode }
        }
        merchandise {
          ... on ProductVariant {
            id
            title
            image { url altText }
            price { amount currencyCode }
            product {
              title
              handle
            }
          }
        }
      }
    }
  }
`;

const normalizeCart = (cart) => ({
  ...cart,
  lines: cart.lines.edges.map((e) => e.node)
});

// Handles the userErrors half of a cart mutation's response the same way
// everywhere -- Storefront API cart mutations return 200 with an empty
// `cart`/populated `userErrors` array on a logical failure (e.g. an
// out-of-stock variant), rather than a GraphQL-level error.
const resultFromMutation = (payload) => {
  const { cart, userErrors } = payload;
  if (userErrors.length > 0) {
    return { success: false, error: userErrors.map((e) => e.message).join("; ") };
  }
  return { success: true, cart: normalizeCart(cart) };
};

export const createCart = async (variantId, quantity = 1) => {
  try {
    const query = `
      mutation CartCreate($input: CartInput) {
        cartCreate(input: $input) {
          cart { ${CART_FIELDS} }
          userErrors { field message }
        }
      }
    `;
    const variables = { input: { lines: [{ merchandiseId: variantId, quantity }] } };
    const data = await shopifyFetch(query, variables);
    return resultFromMutation(data.cartCreate);
  } catch (error) {
    console.error("Shop Transmission Error: ", error);
    return { success: false, error };
  }
};

export const fetchCart = async (cartId) => {
  try {
    const query = `
      query FetchCart($id: ID!) {
        cart(id: $id) { ${CART_FIELDS} }
      }
    `;
    const data = await shopifyFetch(query, { id: cartId });
    // A stale/expired cart id (Shopify carts expire after ~10 days idle)
    // resolves to a null cart rather than an error -- callers use `notFound`
    // to know they should start a fresh cart instead of retrying.
    if (!data.cart) return { success: false, notFound: true };
    return { success: true, cart: normalizeCart(data.cart) };
  } catch (error) {
    console.error("Shop Transmission Error: ", error);
    return { success: false, error };
  }
};

export const addCartLine = async (cartId, variantId, quantity = 1) => {
  try {
    const query = `
      mutation CartLinesAdd($cartId: ID!, $lines: [CartLineInput!]!) {
        cartLinesAdd(cartId: $cartId, lines: $lines) {
          cart { ${CART_FIELDS} }
          userErrors { field message }
        }
      }
    `;
    const variables = { cartId, lines: [{ merchandiseId: variantId, quantity }] };
    const data = await shopifyFetch(query, variables);
    return resultFromMutation(data.cartLinesAdd);
  } catch (error) {
    console.error("Shop Transmission Error: ", error);
    return { success: false, error };
  }
};

// quantity must be >= 1 -- Storefront API rejects 0 here; removing a line
// entirely is a separate mutation (removeCartLine), called by the UI layer
// instead of passing 0 through to this one.
export const updateCartLine = async (cartId, lineId, quantity) => {
  try {
    const query = `
      mutation CartLinesUpdate($cartId: ID!, $lines: [CartLineUpdateInput!]!) {
        cartLinesUpdate(cartId: $cartId, lines: $lines) {
          cart { ${CART_FIELDS} }
          userErrors { field message }
        }
      }
    `;
    const variables = { cartId, lines: [{ id: lineId, quantity }] };
    const data = await shopifyFetch(query, variables);
    return resultFromMutation(data.cartLinesUpdate);
  } catch (error) {
    console.error("Shop Transmission Error: ", error);
    return { success: false, error };
  }
};

export const removeCartLine = async (cartId, lineId) => {
  try {
    const query = `
      mutation CartLinesRemove($cartId: ID!, $lineIds: [ID!]!) {
        cartLinesRemove(cartId: $cartId, lineIds: $lineIds) {
          cart { ${CART_FIELDS} }
          userErrors { field message }
        }
      }
    `;
    const data = await shopifyFetch(query, { cartId, lineIds: [lineId] });
    return resultFromMutation(data.cartLinesRemove);
  } catch (error) {
    console.error("Shop Transmission Error: ", error);
    return { success: false, error };
  }
};
